import { toast } from "$global-providers/toast-provider";
import { useFacilityLocations } from "$routes/inventory/_lib/use-facility-locations";
import { NetworkStatus, useApolloClient, useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useDebounce } from "use-debounce";

import type { InventoryTableFilterState } from "./inventory-filter-state";
import { MoveLotCheckbox } from "./move-drawer/move-lot-checkbox";

import {
  getLotBySkuId,
  INVENTORY_SEARCH_QUERY,
  type InventorySearchResults,
  type Lot,
} from "./queries";

/*
 * As per the docs, networkStatus < 7 is the equivalent of the
 * `loading` variable from useQuery/useMutation equalling true:
 * https://www.apollographql.com/docs/react/api/react/hoc/#datanetworkstatus
 *
 * Passing only `networkStatus` makes the interface simpler, and the
 * logic and tests easier to understand, as you do not have two
 * variables representing the same "real world" state.
 */
function isCacheAndNetworkLoading({
  data,
  networkStatus,
}: {
  data?: unknown;
  networkStatus: NetworkStatus;
}): boolean {
  return (
    (networkStatus < 7 && !data) || networkStatus === NetworkStatus.refetch
  );
}

function useGroupLots({
  ungroupedData,
  facilityId,
  locationId,
  refreshKey,
}: {
  ungroupedData?: InventorySearchResults;
  facilityId: string;
  locationId?: string;
  refreshKey?: number;
}) {
  const client = useApolloClient();
  const { data: locationsData } = useFacilityLocations();
  const { formatMessage } = useIntl();

  const getRowChildren = useCallback(
    async ({ skuId, name, externalReferences }) => {
      const { data, error } = await getLotBySkuId(client, {
        skuId,
        facilityId,
        locationId,
      });

      if (error) {
        toast.error(
          formatMessage({
            id: "inventory.overview.fetchChildrenError",
            defaultMessage: "Something went wrong while fetching lots",
          }),
        );
        return [];
      }

      return data.getLotsBySkuId.map((lot: Lot) => {
        const location = locationsData?.find(
          ({ _id }) => _id === lot.locationId,
        );
        return {
          ...lot,
          name,
          location: location?.name,
          skuId,
          externalReferences,
          __expander: (
            <MoveLotCheckbox
              lotId={lot.id}
              name={name}
              locationId={lot.locationId}
              locationName={location?.name}
              expiryDate={lot.expiryDate.toString()}
              quantity={lot.quantity}
              externalReferences={externalReferences}
            />
          ),
        };
      });
    },
    [client, facilityId, locationId, locationsData, formatMessage],
  );

  const createFetchChildrenFunction = useCallback(
    (
      skuId: string,
      name: string,
      externalReferences?: ReadonlyArray<{ origin: string }>,
    ) =>
      () =>
        getRowChildren({ skuId, name, externalReferences }),
    [getRowChildren],
  );

  return useMemo(() => {
    if (!ungroupedData) {
      return [];
    }

    return (ungroupedData.inventorySearchTmp?.edges ?? []).map(
      (inventorySummaryEdge) => ({
        ...inventorySummaryEdge.node,
        skuId: inventorySummaryEdge.node.id,
        meta: {
          isExpanding: true,
          fetchKey: `${inventorySummaryEdge.node.id}${refreshKey ? `|${refreshKey}` : ""}`,
          fetchChildren: createFetchChildrenFunction(
            inventorySummaryEdge.node.id,
            inventorySummaryEdge.node.name,
            inventorySummaryEdge.node.sku?.externalReferences,
          ),
        },
      }),
    );
  }, [ungroupedData, refreshKey, createFetchChildrenFunction]);
}

type UseSearchLotsArgs = {
  inventoryTableFilterState: InventoryTableFilterState;
  facilityId: string;
  currentPage: number;
  pageSize: number;
};

export function useSearchLots({
  inventoryTableFilterState: {
    locationId,
    defaultInventorySortBy,
    searchTerm,
    sortBy,
    refreshKey,
    itemType,
  },
  facilityId,
  currentPage,
  pageSize,
}: UseSearchLotsArgs) {
  const [searchLots, { data: ungroupedData, error, networkStatus }] =
    useLazyQuery<InventorySearchResults>(INVENTORY_SEARCH_QUERY, {
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
    });

  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);

  useEffect(() => {
    if (facilityId) {
      searchLots({
        variables: {
          name: debouncedSearchTerm,
          facilityId,
          locationId,
          itemType,
          orderBy: sortBy?.field ?? defaultInventorySortBy?.field,
          orderDirection: sortBy?.direction,
          pageNumber: currentPage,
          pageSize,
        },
      });
    }
  }, [
    debouncedSearchTerm,
    facilityId,
    locationId,
    itemType,
    searchLots,
    sortBy,
    defaultInventorySortBy,
    currentPage,
    pageSize,
  ]);

  // Hack - to address the bug in api-inventory where if you go past the number of pages
  // there are, the totalCount becomes null.
  // This ensures that, if it's null, that we use its previous value.
  const [totalRows, setTotalRows] = useState(0);
  useEffect(() => {
    setTotalRows((prevState) => {
      return ungroupedData?.inventorySearchTmp?.totalCount === null ||
        ungroupedData?.inventorySearchTmp?.totalCount === undefined
        ? prevState
        : ungroupedData.inventorySearchTmp.totalCount;
    });
  }, [ungroupedData]);

  const groupedLots = useGroupLots({
    facilityId,
    locationId,
    refreshKey,
    ungroupedData,
  });

  return {
    data: groupedLots,
    totalRows,
    loading: isCacheAndNetworkLoading({
      data: ungroupedData,
      networkStatus,
    }),
    error,
  };
}
